import React, { useEffect, useState } from "react";
import apiFetch from "./utils/api";
import "./MainPage.css";

const getStatusColor = (status) => {
  switch (status.toLowerCase()) {
    case "up":
      return "#27ae60";
    case "paused":
      return "#95a5a6";
    case "seems down":
      return "#f39c12";
    case "down":
      return "#e74c3c";
    default:
      return "#7f8c8d";
  }
};

const getStatusBgColor = (status) => {
  switch (status.toLowerCase()) {
    case "up":
      return "#d4edda";
    case "paused":
      return "#e2e3e5";
    case "seems down":
      return "#fff3cd";
    case "down":
      return "#f8d7da";
    default:
      return "#f0f0f0";
  }
};

export default function CloudDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [refreshing, setRefreshing] = useState(false);

  const loadData = async (isRefresh = false) => {
    if (isRefresh) setRefreshing(true);
    else setLoading(true);

    setError("");
    try {
      const result = await apiFetch("/api/monitors/cloud-dashboard");
      if (result) setData(result);
    } catch (e) {
      setError(e && e.message ? e.message : "Failed to load monitors");
    } finally {
      if (isRefresh) setRefreshing(false);
      else setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
    // Refresh every 60 seconds
    const interval = setInterval(() => loadData(true), 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  if (loading) return <div style={{ padding: 20 }}>Loading monitors...</div>;
  if (error)
    return (
      <div
        style={{ padding: 20, color: "#e74c3c", fontSize: 14 }}
      >{`Error: ${error}`}</div>
    );
  if (!data || !data.groups || data.groups.length === 0)
    return <div style={{ padding: 20 }}>No monitors found</div>;

  return (
    <div style={{ padding: 20 }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 20,
        }}
      >
        <h2 style={{ margin: 0 }}>Cloud Dashboard</h2>
        <div style={{ fontSize: 12, color: "#7f8c8d" }}>
          <span style={{ marginRight: 12 }}>
            Last updated: {new Date(data.timestamp).toLocaleTimeString()}
          </span>
          <button
            onClick={() => loadData(true)}
            disabled={refreshing}
            style={{
              padding: "6px 12px",
              borderRadius: 4,
              border: "1px solid #bdc3c7",
              background: refreshing ? "#ecf0f1" : "#fff",
              cursor: refreshing ? "default" : "pointer",
              opacity: refreshing ? 0.6 : 1,
            }}
          >
            {refreshing ? "Refreshing..." : "Refresh"}
          </button>
        </div>
      </div>

      <div style={{ marginBottom: 12, fontSize: 13, color: "#7f8c8d" }}>
        Total monitors: <strong>{data.totalMonitors}</strong>
        {data.uptimeWindow?.days ? (
          <span style={{ marginLeft: 12 }}>
            Uptime window: <strong>Last {data.uptimeWindow.days} days</strong>
          </span>
        ) : (
          <span style={{ marginLeft: 12 }}>
            Uptime window: <strong>Last 30 days</strong>
          </span>
        )}
      </div>

      {data.groups.map((group) => (
        <div
          key={group.id}
          style={{
            marginBottom: 24,
            border: "1px solid #ecf0f1",
            borderRadius: 6,
            overflow: "hidden",
          }}
        >
          <div
            style={{
              background: "#f8f9fa",
              padding: "12px 16px",
              borderBottom: "1px solid #ecf0f1",
              fontWeight: 600,
              fontSize: 14,
            }}
          >
            {group.name}{" "}
            <span style={{ fontSize: 12, color: "#7f8c8d" }}>
              ({group.monitors.length})
            </span>
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))",
              gap: 0,
            }}
          >
            {group.monitors.map((monitor) => (
              <div
                key={monitor.id}
                style={{
                  padding: 16,
                  borderRight: "1px solid #ecf0f1",
                  borderBottom: "1px solid #ecf0f1",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "start",
                    marginBottom: 12,
                  }}
                >
                  <div style={{ flex: 1 }}>
                    <div
                      style={{
                        fontWeight: 600,
                        fontSize: 14,
                        marginBottom: 4,
                        wordBreak: "break-word",
                      }}
                    >
                      {monitor.name}
                    </div>
                    {monitor.url && (
                      <div style={{ fontSize: 12, color: "#7f8c8d" }}>
                        {monitor.url}
                      </div>
                    )}
                  </div>
                  <div
                    style={{
                      padding: "4px 8px",
                      borderRadius: 4,
                      background: getStatusBgColor(monitor.status),
                      color: getStatusColor(monitor.status),
                      fontSize: 12,
                      fontWeight: 500,
                      whiteSpace: "nowrap",
                      marginLeft: 8,
                    }}
                  >
                    {monitor.status}
                  </div>
                </div>

                <div
                  style={{
                    display: "flex",
                    gap: 16,
                    fontSize: 13,
                    color: "#2c3e50",
                  }}
                >
                  {monitor.uptimePercent !== null && (
                    <div>
                      <div style={{ color: "#7f8c8d", marginBottom: 2 }}>
                        Uptime (30d)
                      </div>
                      <div style={{ fontWeight: 600, fontSize: 14 }}>
                        {typeof monitor.uptimePercent === "number"
                          ? `${monitor.uptimePercent.toFixed(2)}%`
                          : "n/a"}
                      </div>
                    </div>
                  )}
                  {monitor.lastCheckTime && (
                    <div>
                      <div style={{ color: "#7f8c8d", marginBottom: 2 }}>
                        Last Check
                      </div>
                      <div style={{ fontSize: 12 }}>
                        {new Date(
                          monitor.lastCheckTime * 1000,
                        ).toLocaleTimeString()}
                      </div>
                    </div>
                  )}
                  {monitor.alertContacts > 0 && (
                    <div>
                      <div style={{ color: "#7f8c8d", marginBottom: 2 }}>
                        Alerts
                      </div>
                      <div style={{ fontWeight: 600, fontSize: 14 }}>
                        {monitor.alertContacts}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
